import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * AnalyticsTracker dynamically loads and manages third-party integrations:
 * - Google Analytics 4 (GA4) with dynamic SPA virtual page views
 * - Google Tag Manager (GTM)
 * - Microsoft Clarity
 * 
 * Simply add the IDs to .env/environment secrets, and they activate immediately.
 */
export default function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    const metaEnv = (import.meta as any).env || {};
    const isProd = metaEnv.PROD;
    const gaId = metaEnv.VITE_GA_MEASUREMENT_ID;
    const gtmId = metaEnv.VITE_GTM_CONTAINER_ID;
    const clarityId = metaEnv.VITE_CLARITY_ID;
    const anyWindow = window as any;

    const isBot = /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator.userAgent);
    anyWindow.dataLayer = anyWindow.dataLayer || [];

    // Track SPA navigation virtual pageview safely
    const trackPageView = () => {
      if (isBot) return;
      // GA4 Virtual Page View
      if (gaId && gaId.trim() !== '' && anyWindow.gtag) {
        anyWindow.gtag('config', gaId, {
          page_path: location.pathname + location.search,
          page_title: document.title
        });
      }
      // GTM / general dataLayer virtual page view
      anyWindow.dataLayer.push({
        event: 'virtual_pageview',
        page_path: location.pathname + location.search,
        page_title: document.title,
        page_location: window.location.href
      });
    };

    // If analytics are already initialized, track route changes immediately
    if (anyWindow.__analytics_loaded) {
      trackPageView();
    } else {
      // Define deferred initialization function
      const initializeScripts = () => {
        if (anyWindow.__analytics_loaded) return;
        anyWindow.__analytics_loaded = true;

        if (isBot) {
          // Bots do not get third-party tracking scripts injected
          return;
        }

        // 1. Google Analytics 4 (GA4) Initialization
        if (gaId && gaId.trim() !== '' && !document.getElementById('google-analytics-gtag')) {
          const script1 = document.createElement('script');
          script1.async = true;
          script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
          script1.id = 'google-analytics-gtag';
          document.head.appendChild(script1);

          const script2 = document.createElement('script');
          script2.id = 'google-analytics-init';
          script2.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            window.gtag = gtag;
            gtag('js', new Date());
            gtag('config', '${gaId}', { page_path: window.location.pathname });
          `;
          document.head.appendChild(script2);
          console.log('✓ Google Analytics 4 (GA4) initialized (deferred)');
        }

        // 1.1 Google Ads Conversion Tracking (AW-18153888611) Initialization (deferred)
        if (!document.getElementById('google-ads-gtag')) {
          const adsScript = document.createElement('script');
          adsScript.async = true;
          adsScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-18153888611';
          adsScript.id = 'google-ads-gtag';
          document.head.appendChild(adsScript);

          const adsInit = document.createElement('script');
          adsInit.id = 'google-ads-init';
          adsInit.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            window.gtag = gtag;
            gtag('js', new Date());
            gtag('config', 'AW-18153888611');
          `;
          document.head.appendChild(adsInit);
          console.log('✓ Google Ads Conversion tracking initialized (deferred)');
        }

        // 2. Google Tag Manager (GTM) Initialization with strict prefix validation to avoid 404s
        if (gtmId && gtmId.trim() !== '' && gtmId.startsWith('GTM-') && !document.getElementById('google-tag-manager')) {
          const gtmScript = document.createElement('script');
          gtmScript.id = 'google-tag-manager';
          gtmScript.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId}');
          `;
          document.head.appendChild(gtmScript);
          console.log('✓ Google Tag Manager (GTM) container injected (deferred)');
        } else if (gtmId && gtmId.trim() !== '' && !gtmId.startsWith('GTM-')) {
          console.warn(`⚠️ Google Tag Manager ID "${gtmId}" does not start with GTM-. Skipped GTM tag load to prevent 404s. Please check if you meant to use VITE_GA_MEASUREMENT_ID.`);
        }

        // 3. Microsoft Clarity Initialization (Production and non-bot only)
        if (isProd && !isBot && clarityId && clarityId.trim() !== '' && !document.getElementById('microsoft-clarity')) {
          const clarityScript = document.createElement('script');
          clarityScript.id = 'microsoft-clarity';
          clarityScript.innerHTML = `
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${clarityId}");
          `;
          document.head.appendChild(clarityScript);
          console.log('✓ Microsoft Clarity tracking code active (deferred)');
        }

        // Trigger initial pageview after scripts injected
        setTimeout(trackPageView, 100);
      };

      // Set up lazy tracking triggering (production triggers on user gesture or 5s fallback; dev after 1s)
      if (isProd) {
        let isLoaded = false;
        const triggerLoad = () => {
          if (isLoaded) return;
          isLoaded = true;
          window.removeEventListener('scroll', triggerLoad);
          window.removeEventListener('click', triggerLoad);
          window.removeEventListener('touchstart', triggerLoad);
          window.removeEventListener('mousemove', triggerLoad);
          
          // Use requestIdleCallback if available for optimal UI fluidness
          if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(() => initializeScripts(), { timeout: 2000 });
          } else {
            setTimeout(initializeScripts, 50);
          }
        };

        window.addEventListener('scroll', triggerLoad, { passive: true });
        window.addEventListener('click', triggerLoad, { passive: true });
        window.addEventListener('touchstart', triggerLoad, { passive: true });
        window.addEventListener('mousemove', triggerLoad, { passive: true });

        const handleLoadFallback = () => {
          setTimeout(triggerLoad, 5000);
          window.removeEventListener('load', handleLoadFallback);
        };

        if (document.readyState === 'complete') {
          setTimeout(triggerLoad, 5000);
        } else {
          window.addEventListener('load', handleLoadFallback);
        }
      } else {
        // Dev environment: Initialize in 1 second to keep development cycles fluid
        setTimeout(initializeScripts, 1000);
      }
    }

    // Global Event Listener for Outbound Link Tracking (WhatsApp & Phone)
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Traverse up to find an anchor tag if click was on a child element (like an SVG or span)
      const anchor = target.closest('a');
      
      if (anchor) {
        const href = anchor.getAttribute('href') || '';
        const anyWindow = window as any;
        anyWindow.dataLayer = anyWindow.dataLayer || [];

        if (href.includes('wa.me') || href.includes('whatsapp.com')) {
          anyWindow.dataLayer.push({
            event: 'whatsapp_click',
            click_type: 'whatsapp',
            link_url: href,
            link_text: anchor.innerText?.trim() || '',
            page_path: window.location.pathname,
            page_title: document.title,
            button_location: anchor.closest('header') ? 'header' : anchor.closest('footer') ? 'footer' : 'body'
          });
        } else if (href.startsWith('tel:')) {
          anyWindow.dataLayer.push({
            event: 'phone_call_click',
            click_type: 'phone',
            link_url: href,
            link_text: anchor.innerText?.trim() || '',
            page_path: window.location.pathname,
            page_title: document.title,
            button_location: anchor.closest('header') ? 'header' : anchor.closest('footer') ? 'footer' : 'body'
          });
        }
      }
    };

    document.addEventListener('click', handleGlobalClick, { capture: true });

    return () => {
      document.removeEventListener('click', handleGlobalClick, { capture: true });
    };
  }, [location]);

  return null; // This is a tracker component, rendering nothing visually
}
